import { Grid, Typography } from '@mui/material';
import { ALL, Lite, QueryFilterOperators } from 'types/api';
import { ShipmentSchema } from 'types/clientV2/shipments';
import { statusTextMapper } from 'constants/datagrid';
import {
  useGetShipmentsClientV2Query,
  useUpdateShipmentsMutation,
} from 'store/slices/clientV2/shipments';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import Chip from 'ui-component/extended/Chip';
import { statusColorCallback } from 'ui-component/DataGrid/GridColTypeDef';
import EditInline from 'ui-component/EditInline';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';
import InfoSectionItem, {
  InfoSectionItemFormatTypes,
} from 'ui-component/InfoSectionItem';
import ShipmentLineStockLotsGrid from 'views/kitting/Kit/RelatedShipmentTab/ShipmentLineStockLotsGrid';
import AddressCard from 'ui-component/cards/AddressCard';

export type ShipmentSchemaExpanded = Lite<
  ShipmentSchema,
  'shipTo' | 'shipFrom'
>;

type RelatedShipmentTabProps = {
  shipmentId: string;
  shipmentLines: DecoratedKitRequestLine['shipmentLines'];
  isLoadingShipmentLines: boolean;
};

export default function RelatedShipmentTab({
  shipmentId,
  shipmentLines,
  isLoadingShipmentLines,
}: RelatedShipmentTabProps) {
  const { isCofactr } = useIsCofactr();

  const {
    data: { data: [shipment] } = { data: [] },
    isLoading: isLoadingShipment,
  } = useServerSideQuery<ShipmentSchema, ShipmentSchemaExpanded>(
    useGetShipmentsClientV2Query,
    {
      schema: [
        ALL,
        {
          shipTo: [],
          shipFrom: [],
          address: [],
          internalMetadata: [],
          externalMetadata: [],
        },
      ],
      filters: [
        {
          field: 'id',
          operator: QueryFilterOperators.is,
          value: shipmentId,
        },
      ],
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid size={4}>
        <InfoSectionItem
          caption="Status"
          formatType={InfoSectionItemFormatTypes.children}
          isLoading={isLoadingShipment}
        >
          <Chip
            label={statusTextMapper[shipment?.status]}
            chipcolor={statusColorCallback(statusTextMapper[shipment?.status])}
            style={{ textTransform: 'uppercase' }}
            sx={{ mb: 2 }}
          />
        </InfoSectionItem>
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Shipped From"
          isLoading={isLoadingShipment}
          formatType={InfoSectionItemFormatTypes.children}
          value
        >
          {shipment?.shipFrom ? (
            <AddressCard location={shipment?.shipFrom} />
          ) : (
            <Typography variant="h4">Not specified</Typography>
          )}
        </InfoSectionItem>
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Shipped At"
          formatType={InfoSectionItemFormatTypes.dateMedium}
          isLoading={isLoadingShipment}
          value={shipment?.shippedAt}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Delivered At"
          formatType={InfoSectionItemFormatTypes.dateMedium}
          isLoading={isLoadingShipment}
          value={shipment?.receivedAt}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Carrier/Service"
          formatType={InfoSectionItemFormatTypes.string}
          isLoading={isLoadingShipment}
          value={`${shipment?.carrier ?? '-'} - ${shipment?.service ?? '-'}`}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Tracking Number"
          formatType={InfoSectionItemFormatTypes.string}
          isLoading={isLoadingShipment}
          value={shipment?.trackingNumber}
        />
      </Grid>
      <Grid size={4}>
        <InfoSectionItem
          caption="Notes"
          formatType={InfoSectionItemFormatTypes.richText}
          isLoading={isLoadingShipment}
          value={shipment?.externalNotes ?? '-'}
          editInline={
            <EditInline
              useMutation={useUpdateShipmentsMutation}
              name="externalNotes"
              hideLabel
              value={shipment?.externalNotes}
              id={shipment?.id}
              submitAsClientV2Api
              fullHeight
            >
              <HookFormQuillInput disableRequiredProps hideLabel />
            </EditInline>
          }
        />
      </Grid>
      {isCofactr && (
        <Grid size={4}>
          <InfoSectionItem
            caption="Internal Notes"
            isLoading={isLoadingShipment}
            formatType={InfoSectionItemFormatTypes.richText}
            value={shipment?.internalNotes}
            editInline={
              <EditInline
                useMutation={useUpdateShipmentsMutation}
                name="internalNotes"
                label="Internal Notes"
                hideLabel
                value={shipment?.internalNotes}
                id={shipment?.id}
                submitAsClientV2Api
                fullHeight
              >
                <HookFormQuillInput disableRequiredProps hideLabel />
              </EditInline>
            }
          />
        </Grid>
      )}
      <Grid size={12}>
        <ShipmentLineStockLotsGrid
          shipment={shipment}
          lines={shipmentLines}
          disabled={isLoadingShipmentLines}
          isLoading={isLoadingShipmentLines}
          showMerge={false}
        />
      </Grid>
    </Grid>
  );
}
